import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import JSZip from 'jszip';
import ThemeSwitcher from '../components/ThemeSwitcher';
import UploadToast from '../components/UploadToast';
import CardDesigner from '../components/CardDesigner';
import CardExportModal from '../components/CardExportModal';
import DeliveryReport from '../components/DeliveryReport';
import {
  getProfiles, getUsernames, patchStatus, bulkPatchStatus, deleteProfile,
  downloadUserZip, getAdminStats, authUsers, authRegister, authUpdateUser,
  authToggleActive, exportExcel, getClasses, uploadMasterImage,
  getTemplates, createTemplate, updateTemplate, deleteTemplate,
  bulkDeleteProfiles,
  proxyImage,
} from '../utils/api';
import api from '../utils/api';

const BASE_URL = process.env.REACT_APP_API_URL || '';
const STATUS_OPTIONS = ['unprocessed', 'inprocess', 'processed'];
const STATUS_COLOR = { unprocessed:'var(--success)', inprocess:'var(--warning)', processed:'var(--primary)' };

const ALL_FIELD_LABELS = {
  srNo:'Sr No', jobNo:'Job No', username:'Username', name:'Name',
  prnCode:'PRN Code', dob:'DOB', class:'Class', mobile:'Mobile',
  address:'Address', bloodGroup:'Blood Group',
  fatherName:'Father Name', fatherContactNo:'Father Contact No',
  motherName:'Mother Name', motherContactNo:'Mother Contact No',
  emergencyName:'Emergency Name', emergencyContactNo:'Emergency Contact No',
  designation:'Designation', employeeCode:'Employee Code',
  doj:'DOJ', dor:'DOR', department:'Department',
  aadhar:'Aadhar', pan:'PAN', rfidCode:'RFID Code',
  barCode:'Bar Code', qrCode:'QR Code', photoName:'Photo Name', signName:'Sign Name', authSignName:'Auth Sign Name', status:'Status',
};

const EMPTY_USER = {
  username:'', password:'', role:'user', isActive:true, educationalYear:'',
  schoolNames: Array(10).fill(''),
  address:'', affiliationCode:'', contactNo:'', email:'',
  securityQuestion:'', securityAnswer:'',
};

function getEduYears() {
  const y = new Date().getFullYear();
  return Array.from({length:7},(_,i)=>y-3+i).map(n=>`${n}-${String(n+1).slice(-2)}`);
}

export default function AdminDashboard() {
  const year = new Date().getFullYear();
  const EDU_YEARS = getEduYears();

  // ── Tab & user selection ──────────────────────────────────────────────────────
  const [tab,          setTab]          = useState('users');
  const [profileTab,   setProfileTab]   = useState('profiles'); // 'profiles' | 'designer' when user selected
  const [selectedUser, setSelectedUser] = useState(null);

  // ── Profile state ─────────────────────────────────────────────────────────────
  const [profiles,     setProfiles]     = useState([]);
  const [total,        setTotal]        = useState(0);
  const [pages,        setPages]        = useState(1);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterJob,    setFilterJob]    = useState('');
  const [filterClass,  setFilterClass]  = useState('');
  const [sortBy,       setSortBy]       = useState('srNo');
  const [sortOrder,    setSortOrder]    = useState('asc');
  const [search,       setSearch]       = useState('');
  const [jobOptions,   setJobOptions]   = useState([]);
  const [classOptions, setClassOptions] = useState([]);

  // ── Multi-select ──────────────────────────────────────────────────────────────
  const [selectedIds,  setSelectedIds]  = useState(() => new Set());
  const [bulkStatus,   setBulkStatus]   = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
const [bulkDeleting, setBulkDeleting] = useState(false);
  // ── Stats & usernames ─────────────────────────────────────────────────────────
  const [stats,     setStats]     = useState(null);
  const [usernames, setUsernames] = useState([]);

  // ── Users ─────────────────────────────────────────────────────────────────────
  const [users,        setUsers]        = useState([]);
  const [userSortBy,   setUserSortBy]   = useState('createdAt');
  const [userOrder,    setUserOrder]    = useState('desc');
  const [filterActive, setFilterActive] = useState('true'); // default: active users only
  const [newUser,      setNewUser]      = useState(EMPTY_USER);
  const [creatingUser, setCreatingUser] = useState(false);
  const [editUser,     setEditUser]     = useState(null);
  const [editUserForm, setEditUserForm] = useState({});
  const [savingUser,   setSavingUser]   = useState(false);

  // ── Download ──────────────────────────────────────────────────────────────────
  const [downloadState, setDownloadState] = useState(null);

  // ── Export ────────────────────────────────────────────────────────────────────
  const [showExport,     setShowExport]     = useState(false);
  const [exportFields,   setExportFields]   = useState(() => Object.keys(ALL_FIELD_LABELS));
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingImages, setExportingImages] = useState(false);
// ── School / class filtering ────────────────────────────────────────────────
const [allProfilesForUser, setAllProfilesForUser] = useState([]);
const [filterSchool, setFilterSchool] = useState('');
const [schoolOptions, setSchoolOptions] = useState([]);

  // ── Card Designer ─────────────────────────────────────────────────────────────
  const [templates,      setTemplates]      = useState([]);
  const [showDesigner,   setShowDesigner]   = useState(false);
  const [designerTmpl,   setDesignerTmpl]   = useState(null);
  const [showCardExport, setShowCardExport] = useState(false);

  // ── Master Sample Design ──────────────────────────────────────────────────────
  const [showMasterDesign,  setShowMasterDesign]  = useState(false);
  const [masterDesignJob,   setMasterDesignJob]   = useState(null);
  const [masterDesignList,  setMasterDesignList]  = useState([]);
  const [masterUploading,   setMasterUploading]   = useState(false);
  const masterSignRef = useRef(null);
  const masterRefRef  = useRef(null);

  // ── Fetch helpers ─────────────────────────────────────────────────────────────
  const fetchMeta = useCallback(async () => {
    try {
      const [uRes, sRes, clsRes] = await Promise.all([
        getUsernames(), getAdminStats(), getClasses(),
      ]);
      setUsernames(uRes.data); setStats(sRes.data); setClassOptions(clsRes.data);
    } catch {}
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const params = { sortBy: userSortBy, order: userOrder };
      if (filterActive !== '') params.isActive = filterActive;
      const { data } = await authUsers(params);
      setUsers(data);
    } catch { toast.error('Failed to load users'); }
  }, [userSortBy, userOrder, filterActive]);

  // const fetchProfiles = useCallback(async () => {
  //   if (!selectedUser) return;
  //   setLoading(true);
  //   try {
  //     const params = { page, limit:15, sortBy, order:sortOrder, username: selectedUser };
  //     if (filterStatus) params.status      = filterStatus;
  //     if (filterJob)    params.jobNo       = filterJob;
  //     if (filterClass)  params.filterClass = filterClass;
  //     if (search)       params.search      = search;
  //     const { data } = await getProfiles(params);
  //     setProfiles(data.profiles); setTotal(data.total); setPages(data.pages);
  //   } catch { toast.error('Failed to load profiles'); }
  //   finally { setLoading(false); }
  // }, [page, selectedUser, filterStatus, filterJob, filterClass, sortBy, sortOrder, search]);
const fetchProfiles = useCallback(async () => {
  if (!selectedUser) return;
  setLoading(true);
  try {
    const params = { page, limit:15, sortBy, order:sortOrder, username: selectedUser };
    if (filterStatus) params.status      = filterStatus;
    if (filterJob)    params.jobNo       = filterJob;
    if (filterClass)  params.filterClass = filterClass;
    if (filterSchool) params.school      = filterSchool;   // <── ADD THIS
    if (search)       params.search      = search;

    const { data } = await getProfiles(params);
    setProfiles(data.profiles);
    setTotal(data.total);
    setPages(data.pages);
  } catch {
    toast.error('Failed to load profiles');
  } finally {
    setLoading(false);
  }
}, [page, selectedUser, filterStatus, filterJob, filterClass, filterSchool, sortBy, sortOrder, search]);
//                                                              ↑ add filterSchool to deps
  const fetchJobOptions = useCallback(async () => {
    if (!selectedUser) return;
    try {
      const { data } = await getProfiles({ limit:10000, page:1, username: selectedUser });
      const jobs = [...new Set(data.profiles.map(p=>p.jobNo).filter(Boolean))].sort((a,b)=>a-b);
      setJobOptions(jobs);
    } catch {}
  }, [selectedUser]);

  // const fetchTemplates = useCallback(async () => {
  //   try {
  //     // Fetch templates for the selected user (or all if no user selected)
  //     const { data } = await getTemplates(selectedUser || undefined);
  //     setTemplates(data);
  //   } catch(e) {
  //     console.error('fetchTemplates error:', e);
  //   }
  // }, [selectedUser]);
const fetchTemplates = useCallback(async () => {
  try {
    const { data } = await getTemplates(selectedUser || undefined);
    console.log('fetchTemplates → selectedUser:', selectedUser, '| count:', data.length, '| data:', data);
    setTemplates(data);
  } catch(e) {
    console.error('fetchTemplates error:', e);
  }
}, [selectedUser]);
  useEffect(() => { fetchMeta(); },      [fetchMeta]);
  useEffect(() => { fetchUsers(); },     [fetchUsers]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  // Reset profileTab when user changes
  useEffect(() => { setProfileTab('profiles'); }, [selectedUser]);
  useEffect(() => {
    if (selectedUser) { setPage(1); }
  }, [selectedUser, filterStatus, filterJob, filterClass, sortBy, sortOrder, search]);
  useEffect(() => { fetchProfiles(); },   [fetchProfiles]);
  useEffect(() => { fetchJobOptions(); }, [fetchJobOptions]);

  // ── Load all profiles for the selected user (for school / class filtering) ──
useEffect(() => {
  if (!selectedUser) {
    setAllProfilesForUser([]);
    setSchoolOptions([]);
    setFilterSchool('');
    return;
  }

  const fetchAllProfiles = async () => {
    try {
      const { data } = await getProfiles({ username: selectedUser, limit: 10000, page: 1 });
      setAllProfilesForUser(data.profiles);

      // Extract distinct schools
      const schools = [...new Set(data.profiles.map(p => p.school).filter(Boolean))].sort();
      setSchoolOptions(schools);
    } catch {
      setAllProfilesForUser([]);
      setSchoolOptions([]);
    }
  };

  fetchAllProfiles();
}, [selectedUser]);
// ── Compute classes based on selected school ──────────────────────────────────
useEffect(() => {
  let classes = allProfilesForUser.map(p => p.class).filter(Boolean);
  if (filterSchool) {
    classes = allProfilesForUser
      .filter(p => p.school === filterSchool)
      .map(p => p.class)
      .filter(Boolean);
  }
  const unique = [...new Set(classes)].sort();
  setClassOptions(unique);
}, [allProfilesForUser, filterSchool]);

  // ── Profile actions ───────────────────────────────────────────────────────────
  const handleStatusChange = async (id, status) => {
    try { await patchStatus(id, status); toast.success(`Status → ${status}`); fetchProfiles(); }
    catch { toast.error('Update failed'); }
  };

  const handleBulkStatusChange = async () => {
    if (!selectedIds.size || !bulkStatus) { toast.error('Select profiles and a status'); return; }
    if (!window.confirm(`Change ${selectedIds.size} profile(s) to "${bulkStatus}"?`)) return;
    setBulkUpdating(true);
    try {
      const { data } = await bulkPatchStatus([...selectedIds], bulkStatus);
      toast.success(data.message);
      setSelectedIds(new Set()); setBulkStatus(''); fetchProfiles();
    } catch { toast.error('Bulk update failed'); }
    finally { setBulkUpdating(false); }
  };
  const handleBulkDelete = async () => {
    console.log('handleBulkDelete called with selectedIds:', selectedIds);
    if (!selectedIds.size) {
      toast.error('No profiles selected');
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.size} profile(s) permanently? This action cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await bulkDeleteProfiles([...selectedIds]);
      toast.success(`Deleted ${selectedIds.size} profile(s)`);
      setSelectedIds(new Set());
      fetchProfiles();
      fetchMeta();
    } 
    
    catch (err) {
  console.error('Bulk delete error:', err);
  const msg = err.response?.data?.message || err.message || 'Bulk delete failed';
  toast.error(msg);
}
    
    
    finally {
      setBulkDeleting(false);
    }
  };
  const toggleSelect = id => setSelectedIds(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleSelectAll = () => {
    const ids = profiles.map(p => p._id);
    const all = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const n = new Set(prev);
      all ? ids.forEach(id => n.delete(id)) : ids.forEach(id => n.add(id));
      return n;
    });
  };
  const handleSelectAllPages = async () => {
    if (!selectedUser) return;
    setSelectingAll(true);
    try {
      const params = { limit:10000, page:1, username: selectedUser };
      if (filterStatus) params.status      = filterStatus;
      if (filterJob)    params.jobNo       = filterJob;
      if (filterClass)  params.filterClass = filterClass;
      if (search)       params.search      = search;
      const { data } = await getProfiles(params);
      setSelectedIds(new Set(data.profiles.map(p => p._id)));
      toast.success(`Selected all ${data.total} profiles`);
    } catch { toast.error('Failed to select all'); }
    finally { setSelectingAll(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await deleteProfile(id); toast.success('Deleted'); fetchProfiles(); fetchMeta(); }
    catch { toast.error('Delete failed'); }
  };

  // ── Navigation: Users ↔ Profiles ─────────────────────────────────────────────
  const handleSelectUser = u => {
    setSelectedUser(u.username);
    setSelectedIds(new Set());
    setPage(1);
    setFilterStatus(''); setFilterJob(''); setFilterClass(''); setSearch('');
  };
  const handleBackToUsers = () => {
    setSelectedUser(null);
    setProfiles([]); setTotal(0); setPages(1);
    setSelectedIds(new Set());
  };

  // ── Sort helpers ──────────────────────────────────────────────────────────────
  const toggleSort = field => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };
  const SortBtn = ({ field, label }) => (
    <span style={{cursor:'pointer',userSelect:'none',display:'flex',alignItems:'center',gap:3}}
      onClick={() => toggleSort(field)}>
      {label}
      <span style={{fontSize:'.6rem',opacity:sortBy===field?1:.3}}>
        {sortBy===field ? (sortOrder==='asc' ? '▲' : '▼') : '⇅'}
      </span>
    </span>
  );
  const toggleUserSort = field => {
    if (userSortBy === field) setUserOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setUserSortBy(field); setUserOrder('asc'); }
  };
  const UserSortBtn = ({ field, label }) => (
    <span style={{cursor:'pointer',userSelect:'none',display:'flex',alignItems:'center',gap:3}}
      onClick={() => toggleUserSort(field)}>
      {label}
      <span style={{fontSize:'.6rem',opacity:userSortBy===field?1:.3}}>
        {userSortBy===field ? (userOrder==='asc' ? '▲' : '▼') : '⇅'}
      </span>
    </span>
  );

  // ── User CRUD ─────────────────────────────────────────────────────────────────
  const handleCreateUser = async e => {
    e.preventDefault();
    if (!newUser.username || !newUser.password) { toast.error('Username and password required'); return; }
    setCreatingUser(true);
    try {
      await authRegister({ ...newUser, schoolNames: newUser.schoolNames.filter(Boolean) });
      toast.success(`User "${newUser.username}" created`);
      setNewUser(EMPTY_USER);
      fetchUsers();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setCreatingUser(false); }
  };

  const openEditUser = u => {
    setEditUser(u);
    setEditUserForm({
      password: '',
      role: u.role,
      isActive: u.isActive,
      educationalYear: u.educationalYear || '',
      schoolNames: [...(u.schoolNames || []), ...Array(10).fill('')].slice(0, 10),
      address: u.address || '',
      affiliationCode: u.affiliationCode || '',
      contactNo: u.contactNo || '',
      email: u.email || '',
      securityQuestion: u.securityQuestion || '',
      securityAnswer: '',
    });
  };

  const handleSaveUser = async () => {
    setSavingUser(true);
    try {
      const payload = { ...editUserForm, schoolNames: editUserForm.schoolNames.filter(Boolean) };
      if (!payload.password) delete payload.password;
      await authUpdateUser(editUser._id, payload);
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch(err) { toast.error(err.response?.data?.message || 'Update failed'); }
    finally { setSavingUser(false); }
  };

  const handleToggleActive = async u => {
    if (!window.confirm(`${u.isActive ? 'Deactivate' : 'Activate'} "${u.username}"?`)) return;
    try {
      await authToggleActive(u._id);
      toast.success(`${u.username} ${u.isActive ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  // ── Download ──────────────────────────────────────────────────────────────────
  const handleDownload = async username => {
    setDownloadState({ username, status:'downloading', progress:0 });
    try {
      const { data } = await downloadUserZip(username, p => {
        setDownloadState(s => s ? { ...s, progress:p } : s);
      });
      const url = URL.createObjectURL(new Blob([data], { type:'application/zip' }));
      const a   = document.createElement('a');
      a.href = url; a.download = `${username}_${year}.zip`; a.click();
      URL.revokeObjectURL(url);
      setDownloadState({ username, status:'done', progress:100 });
    } catch {
      setDownloadState({ username, status:'error', progress:0 });
      toast.error('Download failed');
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    if (!exportFields.length) { toast.error('Select at least one field'); return; }
    setExportingExcel(true);
    const ids = selectedIds.size > 0 ? [...selectedIds] : [];
    try {
      const { data } = await exportExcel(exportFields, ids);
      const url = URL.createObjectURL(new Blob([data], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a   = document.createElement('a');
      const suf = ids.length ? `_${ids.length}profiles` : '_all';
      a.href = url; a.download = `profiles${suf}_${year}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success(ids.length ? `Exported ${ids.length} profiles!` : 'All exported!');
      setShowExport(false);
    } catch { toast.error('Export failed'); }
    finally { setExportingExcel(false); }
  };
  const toggleExportField = key => setExportFields(f => f.includes(key) ? f.filter(x=>x!==key) : [...f,key]);

  // ── Images ZIP (selected profiles' photos) ───────────────────────────────────
  // const handleExportImagesZip = async () => {
  //   if (!selectedIds.size) { toast.error('No profiles selected'); return; }
  //   setExportingImages(true);
  //   try {
  //     // Fetch full profile list for this user so selections spanning multiple
  //     // pages (via "Select all pages") are all resolvable to a photoPath.
  //     let pool = profiles;
  //     const haveAll = [...selectedIds].every(id => profiles.some(p => p._id === id));
  //     if (!haveAll && selectedUser) {
  //       const { data } = await getProfiles({ username: selectedUser, limit: 10000, page: 1 });
  //       pool = data.profiles;
  //     }
  //     const selected = pool.filter(p => selectedIds.has(p._id) && p.photoPath);
  //     if (!selected.length) { toast.error('No photos found in current selection'); return; }

  //     const zip = new JSZip();
  //     let added = 0;
  //     await Promise.all(selected.map(async p => {
  //       try {
  //         const res = await fetch(`${BASE_URL}${p.photoPath}`);
  //         if (!res.ok) return;
  //         const blob = await res.blob();
  //         const ext = (p.photoPath.split('.').pop() || 'jpg').split('?')[0];
  //         const safeName = (p.name || p.srNo || p._id).toString().replace(/[^a-z0-9_-]+/gi, '_');
  //         zip.file(`${safeName}_${p.srNo || p._id}.${ext}`, blob);
  //         added++;
  //       } catch {}
  //     }));
  //     if (!added) { toast.error('Failed to fetch any photos'); return; }
  //     const content = await zip.generateAsync({ type: 'blob' });
  //     const url = URL.createObjectURL(content);
  //     const a = document.createElement('a');
  //     a.href = url; a.download = `profile_photos_${selected.length}_${year}.zip`; a.click();
  //     URL.revokeObjectURL(url);
  //     toast.success(`Zipped ${added} photo${added!==1?'s':''}!`);
  //   } catch {
  //     toast.error('Image export failed');
  //   } finally {
  //     setExportingImages(false);
  //   }
  // };

// const handleExportImagesZip = async () => {
//   if (!selectedIds.size) { toast.error('No profiles selected'); return; }
//   setExportingImages(true);
//   try {
//     // Fetch full profile list if selection spans multiple pages
//     let pool = profiles;
//     const haveAll = [...selectedIds].every(id => profiles.some(p => p._id === id));
//     if (!haveAll && selectedUser) {
//       const { data } = await getProfiles({ username: selectedUser, limit: 10000, page: 1 });
//       pool = data.profiles;
//     }

//     const selected = pool.filter(p => selectedIds.has(p._id) && p.photoPath);
//     if (!selected.length) { toast.error('No photos found in current selection'); return; }

//     const token = localStorage.getItem('token');
//     const zip = new JSZip();
//     let added = 0;

//     await Promise.all(selected.map(async p => {
//       try {
//         // ── Proxy through backend to avoid CORS on Hostinger static files ──
//         const res = await fetch(
//           `${BASE_URL}/api/profiles/proxy-image?p=${encodeURIComponent(p.photoPath)}`,
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//         if (!res.ok) return;
//         const blob = await res.blob();

//         // ── Use photoName-based filename from photoPath (e.g. RAMESH123.jpg) ──
//         const fileName = p.photoPath.split('/').pop();
//         zip.file(fileName, blob);
//         added++;
//       } catch {}
//     }));

//     if (!added) { toast.error('Failed to fetch any photos'); return; }

//     const content = await zip.generateAsync({ type: 'blob' });
//     const url = URL.createObjectURL(content);
//     const a = document.createElement('a');
//     a.href = url;
//     a.download = `profile_photos_${selected.length}_${year}.zip`;
//     a.click();
//     URL.revokeObjectURL(url);
//     toast.success(`Zipped ${added} photo${added !== 1 ? 's' : ''}!`);
//   } catch {
//     toast.error('Image export failed');
//   } finally {
//     setExportingImages(false);
//   }
// };
// const handleExportImagesZip = async () => {
//   if (!selectedIds.size) { toast.error('No profiles selected'); return; }
//   setExportingImages(true);
//   try {
//     // Fetch full profile list if selection spans multiple pages
//     let pool = profiles;
//     const haveAll = [...selectedIds].every(id => profiles.some(p => p._id === id));
//     if (!haveAll && selectedUser) {
//       const { data } = await getProfiles({ username: selectedUser, limit: 10000, page: 1 });
//       pool = data.profiles;
//     }

//     const selected = pool.filter(p => selectedIds.has(p._id) && p.photoPath);
//     if (!selected.length) { toast.error('No photos found in current selection'); return; }

//     const zip = new JSZip();
//     let added = 0;

//     await Promise.all(selected.map(async p => {
//       try {
//         // ── Proxy through backend — token auto-added by axios interceptor ──
//         const res = await proxyImage(p.photoPath);
//         const blob = res.data; // axios responseType:'blob'

//         // ── Use photoName-based filename from photoPath (e.g. RAMESH123.jpg) ──
//         const fileName = p.photoPath.split('/').pop();
//         zip.file(fileName, blob);
//         added++;
//       } catch {}
//     }));

//     if (!added) { toast.error('Failed to fetch any photos'); return; }

//     const content = await zip.generateAsync({ type: 'blob' });
//     const url = URL.createObjectURL(content);
//     const a = document.createElement('a');
//     a.href = url;
//     a.download = `profile_photos_${selected.length}_${year}.zip`;
//     a.click();
//     URL.revokeObjectURL(url);
//     toast.success(`Zipped ${added} photo${added !== 1 ? 's' : ''}!`);
//   } catch {
//     toast.error('Image export failed');
//   } finally {
//     setExportingImages(false);
//   }
// };

const handleExportImagesZip = async () => {
  if (!selectedIds.size) {
    toast.error('No profiles selected');
    return;
  }
  setExportingImages(true);
  try {
    // Build the filter object exactly like fetchProfiles uses
    const filterParams = {
      username: selectedUser,
      limit: 10000,
      page: 1,
    };
    if (filterStatus) filterParams.status = filterStatus;
    if (filterJob)    filterParams.jobNo  = filterJob;
    if (filterClass)  filterParams.filterClass = filterClass;   // backend param name
    if (filterSchool) filterParams.school = filterSchool;       // added school
    if (search)       filterParams.search = search;

    // Fetch all profiles matching the current filters (not just current page)
    const { data } = await getProfiles(filterParams);
    const allFilteredProfiles = data.profiles;

    // Now select only those that are in selectedIds and have a photo
    const selected = allFilteredProfiles.filter(
      p => selectedIds.has(p._id) && p.photoPath
    );

    if (!selected.length) {
      toast.error('No photos found in current selection');
      return;
    }

    const zip = new JSZip();
    let added = 0;

    await Promise.all(selected.map(async p => {
      try {
        const res = await proxyImage(p.photoPath);
        const blob = res.data;
        const fileName = p.photoPath.split('/').pop();
        zip.file(fileName, blob);
        added++;
      } catch {}
    }));

    if (!added) {
      toast.error('Failed to fetch any photos');
      return;
    }

    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profile_photos_${selected.length}_${year}.zip`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Zipped ${added} photo${added !== 1 ? 's' : ''}!`);
  } catch {
    toast.error('Image export failed');
  } finally {
    setExportingImages(false);
  }
};
// ── Card Designer ─────────────────────────────────────────────────────────────
  const handleSaveTemplate = async templateData => {
    try {
      // CardDesigner handles its own update when savedTmplId exists
      // This callback is only called for NEW templates (no _id yet)
      // Strip blob: URLs just in case
      const cleanData = { ...templateData };
      if (cleanData.bgImagePath && (cleanData.bgImagePath.startsWith('blob:') || cleanData.bgImagePath.startsWith('http'))) {
        delete cleanData.bgImagePath;
      }
      if (!cleanData._id) {
        // New template — create for the selected user
        await createTemplate({ ...cleanData, targetUsername: selectedUser || undefined });
      }
      // If _id present, CardDesigner already called updateTemplate directly
      await fetchTemplates();
      setShowDesigner(false);
      setDesignerTmpl(null);
    } catch(e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  // ── Master Sample Design ──────────────────────────────────────────────────────
  const handleOpenMasterDesign = async jobNo => {
    try {
      const { data } = await getProfiles({ username: selectedUser, jobNo, limit:1000, page:1 });
      if (!data.profiles.length) { toast.error('No profiles found for this job'); return; }
      setMasterDesignJob({ jobNo, username: selectedUser });
      setMasterDesignList(data.profiles);
      setShowMasterDesign(true);
    } catch { toast.error('Failed to load job profiles'); }
  };

  const doUploadMasterDesign = async (file, type) => {
    if (!masterDesignList.length) return;
    setMasterUploading(true);
    try {
      await Promise.all(masterDesignList.map(p => uploadMasterImage(p._id, type, file)));
      const label = type==='sign'?'Signature':type==='sign2'?'Auth. Signature':'Reference';
      toast.success(`${label} saved to all Job ${masterDesignJob.jobNo} profiles`);
      fetchProfiles();
    } catch { toast.error('Upload failed'); }
    finally { setMasterUploading(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>🛡️ Admin Dashboard</h1>
          <p>Manage all ID profiles, users, and data exports.</p>
        </div>
        <ThemeSwitcher />
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-card-num">{stats.total}</div>
            <div className="stat-card-label">Total Profiles</div>
          </div>
          {(stats.byStatus||[]).map(s => (
            <div className="stat-card" key={s._id}>
              <div className={`stat-card-num ${s._id==='processed'?'green':s._id==='inprocess'?'orange':'red'}`}>{s.count}</div>
              <div className="stat-card-label" style={{textTransform:'capitalize'}}>{s._id}</div>
            </div>
          ))}
          <div className="stat-card">
            <div className="stat-card-num">{(users||[]).filter(u=>u.isActive).length}</div>
            <div className="stat-card-label">Active Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num">{users.length}</div>
            <div className="stat-card-label">Total Users</div>
          </div>
        </div>
      )}

      {/* Tabs — show Back button when viewing a user's profiles */}
      <div className="tab-row">
        {selectedUser ? (
          <>
            <button className="tab-btn" onClick={handleBackToUsers}>← Back to Users</button>
            <button className={`tab-btn${profileTab==='profiles'?' active':''}`}
              onClick={()=>setProfileTab('profiles')}>
              📋 Profiles — {selectedUser}
            </button>
            <button className={`tab-btn${profileTab==='designer'?' active':''}`}
              onClick={()=>{ setProfileTab('designer'); fetchTemplates(); }}>
              🎨 Card Designer
            </button>
          </>
        ) : (
          <>
            <button className={`tab-btn${tab==='users'?      ' active':''}`} onClick={()=>setTab('users')}>
              👥 Users <span className="tab-count">{users.length}</span>
            </button>
            <button className={`tab-btn${tab==='createuser'? ' active':''}`} onClick={()=>setTab('createuser')}>
              ➕ Create User
            </button>

            <button className={`tab-btn${tab==='delivery'?   ' active':''}`} onClick={()=>setTab('delivery')}>
              📦 Delivery
            </button>
            <button className={`tab-btn${tab==='designer'?' active':''}`} onClick={()=>setTab('designer')}>
              🎨 Designer</button>
            
            <button className={`tab-btn${tab==='download'?   ' active':''}`} onClick={()=>setTab('download')}>
              ⬇️ Download
            </button>
          </>
        )}
      </div>

      {/* ══ PROFILES ══════════════════════════════════════════════════════════ */}
      {selectedUser && profileTab==='profiles' && (
        <div className="card">
          <div style={{marginBottom:12,paddingBottom:10,borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
            <div>
              <h3 style={{margin:0,color:'var(--primary)'}}>📋 Profiles for: {selectedUser}</h3>
              <div style={{fontSize:'.8rem',color:'var(--text-muted)',marginTop:3}}>{total} profile{total!==1?'s':''}</div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary btn-sm" onClick={()=>setShowExport(true)}>
                {selectedIds.size>0?`⬇️ (${selectedIds.size})`:'⬇️ Export'}
              </button>
              <button className="btn btn-outline btn-sm" onClick={handleExportImagesZip}
                disabled={exportingImages || selectedIds.size===0}
                title={selectedIds.size===0?'Select profiles first':'Download selected photos as ZIP'}>
                {exportingImages ? '⏳ Zipping…' : `🖼️ Images ZIP${selectedIds.size>0?` (${selectedIds.size})`:''}`}
              </button>
              <button className="btn btn-outline btn-sm" onClick={()=>setShowCardExport(true)}>🖨️ Cards PDF</button>
            </div>
          </div>

          {/* Filters */}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:8,alignItems:'center'}}>
            <input className="search-input" style={{flex:1,minWidth:160}} placeholder="🔍 Search…"
              value={search} onChange={e=>setSearch(e.target.value)}/>
            <select className="search-input" style={{maxWidth:140}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <select className="search-input" style={{maxWidth:120}} value={filterJob} onChange={e=>setFilterJob(e.target.value)}>
              <option value="">All Jobs</option>
              {jobOptions.map(j=><option key={j} value={j}>Job {j}</option>)}
            </select>
            <select className="search-input" style={{maxWidth:130}} value={filterClass} onChange={e=>setFilterClass(e.target.value)}>
              <option value="">All Classes</option>
              {classOptions.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <button className="btn btn-outline btn-sm" onClick={fetchProfiles}>🔄</button>
          </div>

          {/* Bulk bar */}
          {selectedIds.size > 0 && (
            <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',padding:'8px 12px',marginBottom:10,background:'rgba(var(--primary-rgb),.1)',border:'1px solid rgba(var(--primary-rgb),.3)',borderRadius:'var(--radius-sm)'}}>
              <span style={{fontWeight:700,fontSize:'.85rem',color:'var(--primary)'}}>✅ {selectedIds.size} selected</span>
              {selectedIds.size < total && (
                <button className="btn btn-outline btn-sm" onClick={handleSelectAllPages} disabled={selectingAll}>
                  {selectingAll ? '⏳' : `Select all ${total}`}
                </button>
              )}
              <span style={{color:'var(--text-muted)',fontSize:'.8rem'}}>— Set status:</span>
              <select className="search-input" style={{maxWidth:160,padding:'5px 10px'}} value={bulkStatus} onChange={e=>setBulkStatus(e.target.value)}>
                <option value="">Pick status…</option>
                {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <button className="btn btn-primary btn-sm" onClick={handleBulkStatusChange} disabled={bulkUpdating||!bulkStatus}>
                {bulkUpdating ? '⏳ Updating…' : `Apply to ${selectedIds.size}`}
              </button>
              <button
  className="btn btn-danger btn-sm"
  onClick={handleBulkDelete}
  disabled={bulkDeleting}
>
  {bulkDeleting ? '⏳ Deleting…' : `🗑️ Delete ${selectedIds.size}`}
</button>
              <button className="btn btn-ghost btn-sm" onClick={()=>setSelectedIds(new Set())}>✕ Clear</button>
            </div>
          )}

          {loading ? (
            <div className="loading"><div className="spinner"/><p style={{marginTop:12}}>Loading…</p></div>
          ) : profiles.length===0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <h3>No profiles found for {selectedUser}</h3>
            </div>
          ) : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{width:36}}>
                        <input type="checkbox"
                          style={{accentColor:'var(--primary)',cursor:'pointer',width:15,height:15}}
                          checked={profiles.length>0&&profiles.every(p=>selectedIds.has(p._id))}
                          onChange={toggleSelectAll}/>
                      </th>
                      <th><SortBtn field="srNo"  label="Sr"/></th>
                      <th><SortBtn field="jobNo" label="Job"/></th>
                      <th>Photo</th>
                      <th><SortBtn field="name"  label="Name"/></th>
                      {/* <th><SortBtn field="class" label="Class"/></th> */}
                      <th>Mobile</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profiles.map(p => (
                      <tr key={p._id} style={{background:selectedIds.has(p._id)?'rgba(var(--primary-rgb),.07)':undefined}}>
                        <td>
                          <input type="checkbox" checked={selectedIds.has(p._id)} onChange={()=>toggleSelect(p._id)}
                            style={{accentColor:'var(--primary)',cursor:'pointer',width:15,height:15}}/>
                        </td>
                        <td className="td-mono" style={{color:'var(--primary)',fontWeight:700}}>{p.srNo||'—'}</td>
                        <td><span className="badge badge-blue" style={{fontSize:'.7rem'}}>Job{p.jobNo||'—'}</span></td>
                        <td>
                          {p.photoPath
                            ? <img className="photo-thumb" src={`${BASE_URL}${p.photoPath}`} alt=""
                                onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>
                            : null}
                          <div className="photo-placeholder" style={{display:p.photoPath?'none':'flex'}}>👤</div>
                        </td>
                        <td className="td-bold">{p.name||'—'}</td>
                        {/* <td>{p.class||'—'}</td> */}
                        <td>{p.mobile||'—'}</td>
                        <td>
                          <select value={p.status} onChange={e=>handleStatusChange(p._id,e.target.value)}
                            style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:6,cursor:'pointer',fontWeight:700,fontSize:'.75rem',padding:'4px 8px',fontFamily:'Inter',color:STATUS_COLOR[p.status]||'var(--text)'}}>
                            {STATUS_OPTIONS.map(s=>(
                              <option key={s} value={s} style={{background:'var(--surface)',color:'var(--text)'}}>{s}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <div className="action-btns">
                            <button className="btn btn-outline btn-xs" title="Master design for Job" onClick={()=>handleOpenMasterDesign(p.jobNo)}>🖼️</button>
                            <button className="btn btn-danger btn-xs" onClick={()=>handleDelete(p._id,p.name)}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pages > 1 && (
                <div className="pagination">
                  <button className="page-btn" onClick={()=>setPage(1)} disabled={page===1}>«</button>
                  <button className="page-btn" onClick={()=>setPage(p=>p-1)} disabled={page===1}>‹</button>
                  {Array.from({length:Math.min(pages,7)},(_,i)=>{
                    const n = page<=4 ? i+1 : page-3+i;
                    if (n<1||n>pages) return null;
                    return <button key={n} className={`page-btn${n===page?' current':''}`} onClick={()=>setPage(n)}>{n}</button>;
                  })}
                  <button className="page-btn" onClick={()=>setPage(p=>p+1)} disabled={page===pages}>›</button>
                  <button className="page-btn" onClick={()=>setPage(pages)} disabled={page===pages}>»</button>
                  <span className="page-info">Page {page}/{pages}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══ USERS TAB ═══════════════════════════════════════════════════════════ */}
      {!selectedUser && tab==='users' && (
        <div className="card">
          <div className="card-title">👥 System Users</div>
          <div style={{fontSize:'.82rem',color:'var(--text-muted)',marginBottom:12}}>
            Click a user row to view their ID profiles
          </div>

          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:14,alignItems:'center'}}>
            <input className="search-input" style={{maxWidth:200}} placeholder="🔍 Search username…"
              onChange={e=>{
                const q=e.target.value.trim();
                authUsers({sortBy:userSortBy,order:userOrder,isActive:filterActive,search:q||undefined})
                  .then(r=>setUsers(r.data)).catch(()=>{});
              }}/>
            <select className="search-input" style={{maxWidth:170}} value={userSortBy} onChange={e=>setUserSortBy(e.target.value)}>
              <option value="createdAt">Sort: Created Date</option>
              <option value="username">Sort: Username</option>
              <option value="role">Sort: Role</option>
              <option value="isActive">Sort: Active Status</option>
              <option value="educationalYear">Sort: Edu Year</option>
            </select>
            <button className="btn btn-outline btn-sm" onClick={()=>setUserOrder(o=>o==='asc'?'desc':'asc')}>
              {userOrder==='asc' ? '▲ Asc' : '▼ Desc'}
            </button>
            <select className="search-input" style={{maxWidth:160}} value={filterActive} onChange={e=>setFilterActive(e.target.value)}>
              <option value="">All (Active + Inactive)</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
            <button className="btn btn-outline btn-sm" onClick={fetchUsers}>🔄</button>
            <span style={{color:'var(--text-muted)',fontSize:'.8rem'}}>
              {users.length} user{users.length!==1?'s':''} · {(users||[]).filter(u=>u.isActive).length} active
            </span>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th><UserSortBtn field="username"        label="Username"/></th>
                  <th><UserSortBtn field="role"            label="Role"/></th>
                  <th><UserSortBtn field="isActive"        label="Status"/></th>
                  <th><UserSortBtn field="educationalYear" label="Edu Year"/></th>
                  <th>Schools</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th><UserSortBtn field="createdAt"       label="Created"/></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u,i) => (
                  <tr key={u._id}
                    style={{opacity:u.isActive?1:.6, cursor:'pointer'}}
                    onClick={() => handleSelectUser(u)}>
                    <td className="td-mono">{i+1}</td>
                    <td className="td-bold" style={{color:'var(--primary)'}}>{u.username}</td>
                    <td><span className={`badge ${u.role==='admin'?'badge-red':'badge-green'}`}>{u.role}</span></td>
                    <td><span className={`badge ${u.isActive?'badge-green':'badge-orange'}`}>{u.isActive?'Active':'Inactive'}</span></td>
                    <td className="td-mono">{u.educationalYear||'—'}</td>
                    <td style={{fontSize:'.78rem',color:'var(--text-muted)',maxWidth:160}}>
                      {u.schoolNames?.filter(Boolean).slice(0,2).join(', ')||'—'}
                      {(u.schoolNames?.filter(Boolean).length||0)>2?` +${u.schoolNames.filter(Boolean).length-2} more`:''}
                    </td>
                    <td className="td-mono">{u.contactNo||'—'}</td>
                    <td style={{fontSize:'.78rem'}}>{u.email||'—'}</td>
                    <td className="td-mono">{new Date(u.createdAt).toLocaleDateString('en-IN')}</td>
                    <td onClick={e=>e.stopPropagation()}>
                      <div className="action-btns">
                        <button className="btn btn-outline btn-xs" onClick={()=>openEditUser(u)}>✏️</button>
                        <button className={`btn btn-xs ${u.isActive?'btn-danger':'btn-success'}`}
                          onClick={()=>handleToggleActive(u)} title={u.isActive?'Deactivate':'Activate'}>
                          {u.isActive?'🔴':'🟢'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ CREATE USER TAB ══════════════════════════════════════════════════════ */}
      {!selectedUser && tab==='createuser' && (
        <div className="card">
          <div className="card-title">➕ Create New User</div>
          <form onSubmit={handleCreateUser}>
            <div className="form-grid" style={{marginBottom:14}}>
              <div className="form-section-title">🔐 Login Credentials</div>
              <div className="form-group">
                <label>Username *</label>
                <input className="form-input" required placeholder="username"
                  value={newUser.username} onChange={e=>setNewUser({...newUser,username:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input className="form-input" type="password" required placeholder="password"
                  value={newUser.password} onChange={e=>setNewUser({...newUser,password:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Role</label>
                <select className="form-input" value={newUser.role} onChange={e=>setNewUser({...newUser,role:e.target.value})}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Active Status</label>
                <select className="form-input" value={newUser.isActive?'true':'false'} onChange={e=>setNewUser({...newUser,isActive:e.target.value==='true'})}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="form-section-title">📅 Educational Year</div>
              <div className="form-group span-2">
                <label>Educational Year</label>
                <select className="form-input" value={newUser.educationalYear} onChange={e=>setNewUser({...newUser,educationalYear:e.target.value})}>
                  <option value="">Select year…</option>
                  {EDU_YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                </select>
                <div style={{fontSize:'.75rem',color:'var(--text-muted)',marginTop:4}}>
                  Date range: 01 May {newUser.educationalYear?.split('-')[0]||'YYYY'} – 30 Apr {newUser.educationalYear?parseInt(newUser.educationalYear)+1:'YYYY'}
                </div>
              </div>

              <div className="form-section-title">📋 Contact Information</div>
              <div className="form-group">
                <label>Affiliation Code</label>
                <input className="form-input" placeholder="affiliation code"
                  value={newUser.affiliationCode} onChange={e=>setNewUser({...newUser,affiliationCode:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Contact No</label>
                <input className="form-input" placeholder="contact number"
                  value={newUser.contactNo} onChange={e=>setNewUser({...newUser,contactNo:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Email</label>
                <input className="form-input" type="email" placeholder="email"
                  value={newUser.email} onChange={e=>setNewUser({...newUser,email:e.target.value})}/>
              </div>
              <div className="form-group span-2">
                <label>Address</label>
                <textarea className="form-input" rows={2} placeholder="address"
                  value={newUser.address} onChange={e=>setNewUser({...newUser,address:e.target.value})}/>
              </div>

              <div className="form-section-title">🏫 School Names (up to 10)</div>
              {newUser.schoolNames.map((s,i) => (
                <div key={i} className="form-group">
                  <label>School {i+1}</label>
                  <input className="form-input" placeholder={`School ${i+1}`} value={s}
                    onChange={e=>{const a=[...newUser.schoolNames];a[i]=e.target.value;setNewUser({...newUser,schoolNames:a});}}/>
                </div>
              ))}

              <div className="form-section-title">🔒 Security</div>
              <div className="form-group span-2">
                <label>Security Question</label>
                <input className="form-input" placeholder="e.g. What is your mother's maiden name?"
                  value={newUser.securityQuestion} onChange={e=>setNewUser({...newUser,securityQuestion:e.target.value})}/>
              </div>
              <div className="form-group span-2">
                <label>Security Answer</label>
                <input className="form-input" placeholder="answer"
                  value={newUser.securityAnswer} onChange={e=>setNewUser({...newUser,securityAnswer:e.target.value})}/>
              </div>
            </div>
            <div style={{display:'flex',gap:10}}>
              <button type="submit" className="btn btn-primary" disabled={creatingUser}>
                {creatingUser ? '⏳ Creating…' : '➕ Create User'}
              </button>
              <button type="button" className="btn btn-outline" onClick={()=>setNewUser(EMPTY_USER)}>🗑️ Clear</button>
            </div>
          </form>
        </div>
      )}

      {/* ══ CARD DESIGNER (per-user, shown after selecting a user) ═══════════════ */}
      {selectedUser && profileTab==='designer' && (
        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div>
              <div className="card-title" style={{margin:0}}>🎨 Card Templates — {selectedUser}</div>
              <div style={{fontSize:'.78rem',color:'var(--text-muted)',marginTop:3}}>
                Templates created here belong to <b>{selectedUser}</b>
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={()=>{setDesignerTmpl(null);setShowDesigner(true);}}>
              ➕ New Template
            </button>
          </div>
          {templates.length===0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🎨</div>
              <h3>No templates for {selectedUser}</h3>
              <p>Create the first ID card template for this user.</p>
              <button className="btn btn-primary" style={{marginTop:12}} onClick={()=>{setDesignerTmpl(null);setShowDesigner(true);}}>
                ➕ Create Template
              </button>
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:16}}>
              {templates.map(t => (
                <div key={t._id} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:14}}>
                  <div style={{
                    width:'100%',paddingTop:'63%',position:'relative',
                    background: t.bgImagePath ? `url('${BASE_URL}${t.bgImagePath}') center/cover` : (t.bgColor||'#fff'),
                    borderRadius:'var(--radius-sm)',marginBottom:10,border:'1px solid var(--border)',overflow:'hidden',
                  }}>
                    <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <span style={{fontSize:'1.5rem',opacity:.4}}>🪪</span>
                    </div>
                    <div style={{position:'absolute',top:4,right:4}}>
                      <span className="badge badge-blue" style={{fontSize:'.65rem'}}>{t.cardSide}</span>
                    </div>
                  </div>
                  <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:4}}>{t.name}</div>
                  <div style={{fontSize:'.75rem',color:'var(--text-muted)',marginBottom:10}}>
                    {t.elements?.length||0} elements · {t.cardWidth}×{t.cardHeight}mm
                  </div>
                  <div style={{display:'flex',gap:6}}>
                    <button className="btn btn-outline btn-sm" style={{flex:1}}
                      onClick={()=>{setDesignerTmpl(t);setShowDesigner(true);}}>✏️ Edit</button>
                    <button className="btn btn-danger btn-xs" onClick={async()=>{
                      if(!window.confirm(`Delete template "${t.name}"?`)) return;
                      try { await deleteTemplate(t._id); fetchTemplates(); toast.success('Deleted'); }
                      catch { toast.error('Delete failed'); }
                    }}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ CARD DESIGNER TAB (old main tab — now hidden, kept for reference) ══ */}
      {!selectedUser && tab==='designer' && (
        <div className="card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div className="card-title" style={{margin:0}}>🎨 ID Card Templates</div>
            <button className="btn btn-primary btn-sm" onClick={()=>{setDesignerTmpl(null);setShowDesigner(true);}}>
              ➕ New Template
            </button>
          </div>
          {templates.length===0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🎨</div>
              <h3>No templates yet</h3>
              <p>Create your first ID card template to design and export cards.</p>
              <button className="btn btn-primary" style={{marginTop:12}} onClick={()=>{setDesignerTmpl(null);setShowDesigner(true);}}>
                ➕ Create Template
              </button>
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:16}}>
              {templates.map(t => (
                <div key={t._id} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:14}}>
                  <div style={{
                    width:'100%',paddingTop:'63%',position:'relative',
                    background: t.bgImagePath ? `url('${BASE_URL}${t.bgImagePath}') center/cover` : (t.bgColor||'#fff'),
                    borderRadius:'var(--radius-sm)',marginBottom:10,border:'1px solid var(--border)',overflow:'hidden',
                  }}>
                    <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <span style={{fontSize:'1.5rem',opacity:.4}}>🪪</span>
                    </div>
                    <div style={{position:'absolute',top:4,right:4}}>
                      <span className="badge badge-blue" style={{fontSize:'.65rem'}}>{t.cardSide}</span>
                    </div>
                  </div>
                  <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:4}}>{t.name}</div>
                  <div style={{fontSize:'.75rem',color:'var(--text-muted)',marginBottom:10}}>
                    {t.elements?.length||0} elements · {t.cardWidth}×{t.cardHeight}mm
                  </div>
                  <div style={{display:'flex',gap:6}}>
                    <button className="btn btn-outline btn-sm" style={{flex:1}}
                      onClick={()=>{setDesignerTmpl(t);setShowDesigner(true);}}>✏️ Edit</button>
                    <button className="btn btn-danger btn-xs" onClick={async()=>{
                      if(!window.confirm(`Delete template "${t.name}"?`)) return;
                      try { await deleteTemplate(t._id); fetchTemplates(); toast.success('Deleted'); }
                      catch { toast.error('Delete failed'); }
                    }}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ DELIVERY TAB ═════════════════════════════════════════════════════════ */}
      {!selectedUser && tab==='delivery' && (
        <div className="card">
          <div className="card-title">📦 Delivery Status Report</div>
          <DeliveryReport api={api} isAdmin={true} usernames={usernames} defaultUsername={''}/>
        </div>
      )}


{tab==='designer' && (
  <div className="card">
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
      <div className="card-title" style={{margin:0}}>🎨 My Card Templates</div>
      <button className="btn btn-primary btn-sm"
        onClick={()=>{ setDesignerTmpl(null); setShowDesigner(true); }}>
        ➕ New Template
      </button>
    </div>
    {templates.length === 0 ? (
      <div className="empty-state">
        <div className="empty-state-icon">🎨</div>
        <h3>No templates yet</h3>
        <button className="btn btn-primary" style={{marginTop:12}}
          onClick={()=>{ setDesignerTmpl(null); setShowDesigner(true); }}>
          ➕ Create Template
        </button>
      </div>
    ) : (
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:16}}>
        {templates.map(t => (
          <div key={t._id} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:14}}>
            <div style={{
              width:'100%',paddingTop:'63%',position:'relative',
              background: t.bgImagePath ? `url('${BASE_URL}${t.bgImagePath}') center/cover` : (t.bgColor||'#fff'),
              borderRadius:'var(--radius-sm)',marginBottom:10,border:'1px solid var(--border)',overflow:'hidden',
            }}>
              <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <span style={{fontSize:'1.5rem',opacity:.4}}>🪪</span>
              </div>
              <div style={{position:'absolute',top:4,right:4}}>
                <span className="badge badge-blue" style={{fontSize:'.65rem'}}>{t.cardSide}</span>
              </div>
            </div>
            <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:4}}>{t.name}</div>
            <div style={{fontSize:'.75rem',color:'var(--text-muted)',marginBottom:10}}>
              {t.elements?.length||0} elements · {t.cardWidth}×{t.cardHeight}mm
            </div>
            <div style={{display:'flex',gap:6}}>
              <button className="btn btn-outline btn-sm" style={{flex:1}}
                onClick={()=>{ setDesignerTmpl(t); setShowDesigner(true); }}>
                ✏️ Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}

{/* ══ CARD DESIGNER FULL-SCREEN ═══════════════════════════════════════════ */}
{showDesigner && (
  <div style={{position:'fixed',inset:0,zIndex:2000,background:'var(--bg)',display:'flex',flexDirection:'column'}}>
    <CardDesigner
      template={designerTmpl}
      previewProfile={profiles[0] || null}
      BASE_URL={BASE_URL}
      onClose={()=>{ setShowDesigner(false); setDesignerTmpl(null); }}
      onSave={async (payload) => {
        try {
          if (payload._id) {
            await updateTemplate(payload._id, payload);
          } else {
            await createTemplate(payload);
          }
          toast.success('Template saved!');
          setShowDesigner(false);
          setDesignerTmpl(null);
          fetchTemplates();
        } catch(e) { toast.error(e.response?.data?.message || 'Save failed'); }
      }}
    />
  </div>
)}
      {/* ══ DOWNLOAD TAB ════════════════════════════════════════════════════════ */}
      {!selectedUser && tab==='download' && (
        <div className="card">
          <div className="card-title">⬇️ Download User Data as ZIP</div>
          <p style={{color:'var(--text-muted)',marginBottom:18,fontSize:'.85rem'}}>
            Each ZIP contains an Excel file + all photos for that user.
          </p>
          {usernames.length===0 ? (
            <div className="empty-state"><div className="empty-state-icon">📭</div><h3>No user data yet</h3></div>
          ) : (
            <div className="download-grid">
              {usernames.map(u => {
                const count = stats?.byUser?.find(x=>x._id===u)?.count || 0;
                const isDl  = downloadState?.username===u && downloadState.status==='downloading';
                return (
                  <div key={u} className="download-card">
                    <div style={{fontSize:'1.8rem'}}>👤</div>
                    <div className="download-card-info">
                      <div className="download-card-name">{u}</div>
                      <div className="download-card-meta">{count} profile{count!==1?'s':''}</div>
                      {isDl && (
                        <div style={{marginTop:6}}>
                          <div style={{background:'var(--bg3)',borderRadius:99,height:4,overflow:'hidden'}}>
                            {downloadState.progress===-1
                              ? <div style={{height:'100%',width:'40%',background:`linear-gradient(90deg,transparent,var(--primary),transparent)`,animation:'shimmer 1.5s infinite',borderRadius:99}}/>
                              : <div style={{height:'100%',width:`${downloadState.progress}%`,background:'var(--gradient)',borderRadius:99,transition:'width .3s'}}/>
                            }
                          </div>
                          <div style={{fontSize:'.72rem',color:'var(--text-muted)',marginTop:2}}>
                            {downloadState.progress===-1 ? 'Downloading…' : `${downloadState.progress}%`}
                          </div>
                        </div>
                      )}
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={()=>handleDownload(u)} disabled={isDl}>
                      {isDl ? '⏳' : downloadState?.username===u&&downloadState.status==='done' ? '✅' : '⬇️'} {u}_{year}.zip
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ EXPORT MODAL ════════════════════════════════════════════════════════ */}
      {showExport && (
        <div className="modal-overlay" onClick={()=>setShowExport(false)}>
          <div className="modal" style={{maxWidth:640}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>⬇️ Export {selectedIds.size>0?`${selectedIds.size} Selected`:'All'} Profiles</h2>
              <button className="modal-close" onClick={()=>setShowExport(false)}>✕</button>
            </div>
            <div className="modal-body">
              {selectedIds.size>0 ? (
                <div style={{background:'rgba(var(--primary-rgb),.1)',border:'1px solid rgba(var(--primary-rgb),.3)',borderRadius:'var(--radius-sm)',padding:'8px 12px',marginBottom:12,fontSize:'.82rem',color:'var(--primary)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <span>✅ Exporting <b>{selectedIds.size}</b> selected</span>
                  <button className="btn btn-ghost btn-xs" onClick={()=>setSelectedIds(new Set())}>Clear</button>
                </div>
              ) : (
                <div style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'8px 12px',marginBottom:12,fontSize:'.82rem',color:'var(--text-muted)'}}>
                  📋 Exporting <b>all</b> profiles — select rows to export specific ones
                </div>
              )}
              <div style={{display:'flex',gap:8,marginBottom:14,alignItems:'center'}}>
                <button className="btn btn-outline btn-sm" onClick={()=>setExportFields(Object.keys(ALL_FIELD_LABELS))}>Select All</button>
                <button className="btn btn-outline btn-sm" onClick={()=>setExportFields([])}>Clear All</button>
                <span style={{color:'var(--text-muted)',fontSize:'.78rem'}}>{exportFields.length}/{Object.keys(ALL_FIELD_LABELS).length}</span>
              </div>
              <div className="checkbox-grid">
                {Object.entries(ALL_FIELD_LABELS).map(([key,label]) => (
                  <div key={key} className="checkbox-item" onClick={()=>toggleExportField(key)}>
                    <input type="checkbox" readOnly checked={exportFields.includes(key)}/>
                    <label>{label}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setShowExport(false)}>Cancel</button>
              {/* <button className="btn btn-primary" onClick={handleExport} disabled={exportingExcel||!exportFields.length}>
                {exportingExcel ? '⏳ Exporting…' : `⬇️ Download (${exportFields.length} cols)`}
              </button> */}
              <button
  className="btn btn-primary"
  onClick={handleExport}
  disabled={
    exportingExcel ||
    !exportFields.length ||
    selectedIds.size === 0   // 👈 add this
  }
>
  {exportingExcel ? '⏳ Exporting…' : `⬇️ Download (${exportFields.length} cols)`}
</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ CARD DESIGNER FULL-SCREEN ════════════════════════════════════════════ */}
      {showDesigner && (
        <div style={{position:'fixed',inset:0,zIndex:2000,background:'var(--bg)',display:'flex',flexDirection:'column'}}>
          {/* <CardDesigner
            template={designerTmpl}
            previewProfile={profiles[0] || null}
            BASE_URL={BASE_URL}
            onClose={()=>{ setShowDesigner(false); setDesignerTmpl(null); }}
            onSave={handleSaveTemplate}
          /> */}
          <CardDesigner
  template={designerTmpl}
  previewProfile={profiles[0] || null}
  BASE_URL={BASE_URL}
  targetUsername={selectedUser}   // ← which user this template belongs to
  onClose={()=>{ setShowDesigner(false); setDesignerTmpl(null); }}
  onSave={handleSaveTemplate}
/>
        </div>
      )}

      {/* ══ CARD EXPORT MODAL ════════════════════════════════════════════════════ */}
      {showCardExport && (
        <CardExportModal
          profiles={profiles}
          jobOptions={jobOptions}
          classOptions={classOptions}
          username={selectedUser||''}
          api={api}
          onClose={()=>setShowCardExport(false)}
        />
      )}

      {/* ══ EDIT USER MODAL ══════════════════════════════════════════════════════ */}
      {editUser && (
        <div className="modal-overlay" onClick={()=>setEditUser(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Edit User — {editUser.username}</h2>
              <button className="modal-close" onClick={()=>setEditUser(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-section-title">🔐 Credentials</div>
                <div className="form-group">
                  <label>New Password <span style={{color:'var(--text-muted)',fontWeight:400}}>(leave blank to keep)</span></label>
                  <input className="form-input" type="password" placeholder="new password"
                    value={editUserForm.password||''} onChange={e=>setEditUserForm({...editUserForm,password:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select className="form-input" value={editUserForm.role} onChange={e=>setEditUserForm({...editUserForm,role:e.target.value})}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Active Status</label>
                  <select className="form-input" value={editUserForm.isActive?'true':'false'} onChange={e=>setEditUserForm({...editUserForm,isActive:e.target.value==='true'})}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Educational Year</label>
                  <select className="form-input" value={editUserForm.educationalYear||''} onChange={e=>setEditUserForm({...editUserForm,educationalYear:e.target.value})}>
                    <option value="">Select year…</option>
                    {EDU_YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>

                <div className="form-section-title">📋 Contact</div>
                <div className="form-group">
                  <label>Affiliation Code</label>
                  <input className="form-input" value={editUserForm.affiliationCode||''} onChange={e=>setEditUserForm({...editUserForm,affiliationCode:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label>Contact No</label>
                  <input className="form-input" value={editUserForm.contactNo||''} onChange={e=>setEditUserForm({...editUserForm,contactNo:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-input" type="email" value={editUserForm.email||''} onChange={e=>setEditUserForm({...editUserForm,email:e.target.value})}/>
                </div>
                <div className="form-group span-2">
                  <label>Address</label>
                  <textarea className="form-input" rows={2} value={editUserForm.address||''} onChange={e=>setEditUserForm({...editUserForm,address:e.target.value})}/>
                </div>

                <div className="form-section-title">🏫 School Names</div>
                {(editUserForm.schoolNames||[]).map((s,i) => (
                  <div key={i} className="form-group">
                    <label>School {i+1}</label>
                    <input className="form-input" value={s} onChange={e=>{
                      const a=[...editUserForm.schoolNames]; a[i]=e.target.value;
                      setEditUserForm({...editUserForm,schoolNames:a});
                    }}/>
                  </div>
                ))}

                <div className="form-section-title">🔒 Security</div>
                <div className="form-group span-2">
                  <label>Security Question</label>
                  <input className="form-input" value={editUserForm.securityQuestion||''} onChange={e=>setEditUserForm({...editUserForm,securityQuestion:e.target.value})}/>
                </div>
                <div className="form-group span-2">
                  <label>Security Answer <span style={{color:'var(--text-muted)',fontWeight:400}}>(leave blank to keep)</span></label>
                  <input className="form-input" value={editUserForm.securityAnswer||''} onChange={e=>setEditUserForm({...editUserForm,securityAnswer:e.target.value})}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setEditUser(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveUser} disabled={savingUser}>
                {savingUser ? '⏳ Saving…' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MASTER SAMPLE DESIGN MODAL ══════════════════════════════════════════ */}
      {showMasterDesign && masterDesignJob && (
        <div className="modal-overlay" onClick={()=>setShowMasterDesign(false)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>🖼️ Master Sample Design — Job {masterDesignJob.jobNo}</h2>
              <button className="modal-close" onClick={()=>setShowMasterDesign(false)}>✕</button>
            </div>
            <div className="modal-body" style={{padding:'20px 24px'}}>
              <p style={{color:'var(--text-muted)',fontSize:'.82rem',marginBottom:16}}>
                Applies to all {masterDesignList.length} profile{masterDesignList.length!==1?'s':''} in Job {masterDesignJob.jobNo}.
              </p>
              <div style={{marginBottom:20}}>
                <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:4}}>✍️ Employee Signature</div>
                {masterDesignList[0]?.signName && <div style={{fontSize:'.74rem',color:'var(--text-muted)',marginBottom:8}}>📄 {masterDesignList[0].signName}</div>}
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  {masterDesignList[0]?.signImagePath
                    ? <img src={`${BASE_URL}${masterDesignList[0].signImagePath}`} alt="sign" style={{width:80,height:60,objectFit:'contain',border:'1px solid var(--border)',borderRadius:6,background:'white'}}/>
                    : <div style={{width:80,height:60,border:'2px dashed var(--border)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.4rem'}}>✍️</div>
                  }
                  <button className="btn btn-outline btn-sm" onClick={()=>masterSignRef.current.click()} disabled={masterUploading}>
                    {masterDesignList[0]?.signImagePath?'🔄 Replace':'⬆️ Upload'} Signature
                  </button>
                  <input ref={masterSignRef} type="file" accept="image/*" style={{display:'none'}}
                    onChange={e=>{if(e.target.files[0]){doUploadMasterDesign(e.target.files[0],'sign');e.target.value='';}}}/>
                </div>
              </div>
              <div style={{marginBottom:20}}>
                <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:4}}>🏛️ Authorized Signature</div>
                {masterDesignList[0]?.authSignName && <div style={{fontSize:'.74rem',color:'var(--text-muted)',marginBottom:8}}>📄 {masterDesignList[0].authSignName}</div>}
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  {masterDesignList[0]?.signImagePath2
                    ? <img src={`${BASE_URL}${masterDesignList[0].signImagePath2}`} alt="sign2" style={{width:80,height:60,objectFit:'contain',border:'1px solid var(--border)',borderRadius:6,background:'white'}}/>
                    : <div style={{width:80,height:60,border:'2px dashed var(--border)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.4rem'}}>✍️</div>
                  }
                  <button className="btn btn-outline btn-sm" onClick={()=>{
                    const inp=document.createElement('input');inp.type='file';inp.accept='image/*';
                    inp.onchange=e=>{if(e.target.files[0])doUploadMasterDesign(e.target.files[0],'sign2');};
                    inp.click();
                  }} disabled={masterUploading}>
                    {masterDesignList[0]?.signImagePath2?'🔄 Replace':'⬆️ Upload'} Auth. Signature
                  </button>
                </div>
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:10}}>🎨 Reference Design Image</div>
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  {masterDesignList[0]?.refImagePath
                    ? <img src={`${BASE_URL}${masterDesignList[0].refImagePath}`} alt="ref" style={{width:80,height:60,objectFit:'contain',border:'1px solid var(--border)',borderRadius:6,background:'white'}}/>
                    : <div style={{width:80,height:60,border:'2px dashed var(--border)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.4rem'}}>🎨</div>
                  }
                  <button className="btn btn-outline btn-sm" onClick={()=>masterRefRef.current.click()} disabled={masterUploading}>
                    {masterDesignList[0]?.refImagePath?'🔄 Replace':'⬆️ Upload'} Reference
                  </button>
                  <input ref={masterRefRef} type="file" accept="image/*" style={{display:'none'}}
                    onChange={e=>{if(e.target.files[0]){doUploadMasterDesign(e.target.files[0],'ref');e.target.value='';}}}/>
                </div>
              </div>
              {masterUploading && (
                <div className="loading" style={{padding:12,marginTop:12}}>
                  <div className="spinner"/>
                  <p style={{marginTop:8,fontSize:'.84rem'}}>Uploading to all profiles in Job {masterDesignJob.jobNo}…</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Download toast */}
      {downloadState && downloadState.status!=='done' && (
        <UploadToast
          label={`Downloading ${downloadState.username}_${year}.zip`}
          status={downloadState.status==='error'?'error':'uploading'}
          progress={downloadState.progress}
          detail={downloadState.status==='error'?'Download failed':downloadState.progress===-1?'Preparing ZIP…':`${downloadState.progress}%`}
          onClose={()=>setDownloadState(null)}/>
      )}
      {downloadState?.status==='done' && (
        <UploadToast
          label={`Downloaded ${downloadState.username}_${year}.zip`}
          status="done"
          detail="Saved to Downloads folder"
          onClose={()=>setDownloadState(null)}/>
      )}
    </div>
  );
}
